import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { getCompanyLogoSrc } from "../utils/mediaUrl";

const VisitorLogin = () => {
    const [input, setInput] = useState("");
    const [backgroundImage, setBackgroundImage] = useState(null);
    const [companyName, setCompanyName] = useState("");
    const [nextScreen, setNextScreen] = useState(false);
    const [id, setId] = useState(null);

    // Disclaimer and camera states
    const [showDisclaimer, setShowDisclaimer] = useState(false);
    const [showCamera, setShowCamera] = useState(false);

    const [photoBlob, setPhotoBlob] = useState(null);
    const [photoUrl, setPhotoUrl] = useState(null);
    const [showPhotoPreview, setShowPhotoPreview] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const videoRef = useRef(null);
    const canvasRef = useRef(null);

    // Fetch company info and set background image
    useEffect(() => {
        const fetchCompany = async () => {
            try {
                const res = await fetch("https://api.insightos.co.uk/api/company", {
                    method: "GET",
                    headers: { "Content-Type": "application/json" },
                    credentials: "include"
                });
                const data = await res.json();

                const name = data?.data?.[0]?.CompanyName;
                const logoSrc = getCompanyLogoSrc(data?.data?.[0]);

                setCompanyName(name || "");
                if (logoSrc) setBackgroundImage(logoSrc);
            } catch (err) {
                console.error("Error fetching company:", err);
            }
        };
        fetchCompany();
    }, []);

    // Handle input change and send to API
    const handleInputChange = async (value) => {
        setInput(value);

        // Simple validation: only allow letters and spaces, min 2 chars
        const isValid = /^[a-zA-Z\s]{2,}$/.test(value.trim());

        if (!isValid) {
            setNextScreen(false);
            setShowDisclaimer(false);
            return;
        }

        try {
            const res = await fetch(`https://api.insightos.co.uk/api/authvisitor/${value}`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
            });

            if (!res.ok) {
                throw new Error("Failed to send visitor data");
            }

            const data = await res.json();

            if (data.success) {

                const id = data.id;
                setId(id)
                setNextScreen(true);
                setShowDisclaimer(true); // Show disclaimer modal
            } else {
                setNextScreen(false);
                setShowDisclaimer(false);
            }
        } catch (err) {
            setNextScreen(false);
            setShowDisclaimer(false);
            console.error("Error sending visitor data:", err);
        }
    };

    // Camera logic (no countdown, just take picture button)
    const openCamera = () => {
        setShowCamera(true);
        setPhotoBlob(null);
        setPhotoUrl(null);
        setShowPhotoPreview(false);
        setTimeout(() => {
            if (videoRef.current) {
                navigator.mediaDevices.getUserMedia({ video: true })
                    .then(stream => {
                        videoRef.current.srcObject = stream;
                        videoRef.current.play();
                    })
                    .catch(err => {
                        alert("Could not access camera.");
                        setShowCamera(false);
                    });
            }
        }, 100);
    };

    const closeCamera = () => {
        setShowCamera(false);
        if (videoRef.current && videoRef.current.srcObject) {
            let tracks = videoRef.current.srcObject.getTracks();
            tracks.forEach(track => track.stop());
            videoRef.current.srcObject = null;
        }
    };

    const takePicture = () => {
        if (videoRef.current && canvasRef.current) {
            const context = canvasRef.current.getContext('2d');
            context.drawImage(videoRef.current, 0, 0, 320, 240);
            canvasRef.current.toBlob(async (blob) => {
                if (blob) {
                    setPhotoBlob(blob);
                    const url = URL.createObjectURL(blob);
                    setPhotoUrl(url);
                    setShowPhotoPreview(true);
                    closeCamera();
                    setShowDisclaimer(false);
                }
            }, 'image/jpeg');
        }
    };

    const retakePhoto = () => {
        setPhotoBlob(null);
        setPhotoUrl(null);
        setShowPhotoPreview(false);
        openCamera();
    };

    console.log(id)

 const submitPhoto = async () => {
    if (!photoBlob) return;
    setIsSubmitting(true);
    const formData = new FormData();
    formData.append("file", photoBlob, "visitor.jpg"); // ✅ must match 'file'

    try {
        await fetch(`https://api.insightos.co.uk/api/visitorphoto/${id}`, {
            method: "POST",
            body: formData,
            credentials: "include"
        });
    } catch (err) {
        console.error("Failed to upload photo", err);
    }

    setIsSubmitting(false);
    setShowPhotoPreview(false);
};


    // Disclaimer modal
    const DisclaimerModal = () => (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
            <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                transition={{ type: "spring", stiffness: 300, damping: 25 }}
                className="bg-white rounded-xl p-8 shadow-xl max-w-md w-full flex flex-col items-center"
            >
                <h2 className="text-xl font-bold mb-4 text-gray-800">Photo Disclaimer</h2>
                <p className="mb-6 text-gray-700 text-center">
                    For security and fire safety purposes, we need to take a photo of you whilst you are on our premises.
                </p>
                <label className="flex items-center space-x-2 mb-2">
                    <input
                        type="checkbox"
                        onChange={e => {
                            if (e.target.checked) {
                                setShowDisclaimer(false);
                                openCamera();
                            }
                        }}
                    />
                    <span className="text-gray-800 font-medium">Yes, I agree</span>
                </label>
            </motion.div>
        </div>
    );

    // Camera modal (no countdown, just take picture button)
    const CameraModal = () => (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
            <motion.div
                initial={false}
                animate={false}
                exit={false}
                className="bg-white rounded-xl p-6 shadow-xl flex flex-col items-center"
            >
                <h2 className="text-lg font-bold mb-2">Take your photo</h2>
                <video ref={videoRef} width={320} height={240} className="rounded mb-4" autoPlay />
                <canvas ref={canvasRef} width={320} height={240} style={{ display: "none" }} />
                <div className="flex gap-4">
                    <button
                        className="px-4 py-2 bg-blue-600 text-white rounded"
                        onClick={takePicture}
                    >
                        Take Picture
                    </button>
                    <button
                        className="px-4 py-2 bg-red-500 text-white rounded"
                        onClick={() => {
                            closeCamera();
                            setShowDisclaimer(false);
                            setShowCamera(false);
                        }}
                    >
                        Cancel
                    </button>
                </div>
            </motion.div>
        </div>
    );

    // Photo preview modal
    const PhotoPreviewModal = () => (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
            <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                transition={{ type: "spring", stiffness: 300, damping: 25 }}
                className="bg-white rounded-xl p-6 shadow-xl flex flex-col items-center"
            >
                <h2 className="text-lg font-bold mb-2">Is this photo okay?</h2>
                {photoUrl && (
                    <img
                        src={photoUrl}
                        alt="Your photo"
                        className="rounded mb-4 border border-gray-300"
                        width={320}
                        height={240}
                        style={{ objectFit: "cover" }}
                    />
                )}
                <div className="flex gap-4">
                    <button
                        className="px-4 py-2 bg-gray-300 text-gray-800 rounded"
                        onClick={retakePhoto}
                        disabled={isSubmitting}
                    >
                        Retake
                    </button>
                    <button
                        className="px-4 py-2 bg-blue-600 text-white rounded"
                        onClick={submitPhoto}
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? "Submitting..." : "Submit"}
                    </button>
                </div>
            </motion.div>
        </div>
    );

    // Animated check circle
    const CheckCircle = () => (
        <motion.div
            initial={{ scale: 0, opacity: 0, y: 100 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0, opacity: 0, y: 100 }}
            transition={{ type: "spring", stiffness: 300, damping: 20 }}
            className="fixed bottom-32 left-1/2 -translate-x-1/2 z-40"
        >
            <div className="flex items-center justify-center">
                <div className="w-32 h-32 rounded-full bg-emerald-500 flex items-center justify-center shadow-2xl">
                    <svg
                        className="w-20 h-20 text-white"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={4}
                        viewBox="0 0 48 48"
                    >
                        <circle cx="24" cy="24" r="22" stroke="white" strokeOpacity="0.2" strokeWidth="4" fill="none" />
                        <motion.path
                            d="M16 24l7 7 9-13"
                            stroke="white"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="4"
                            initial={{ pathLength: 0 }}
                            animate={{ pathLength: 1 }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                        />
                    </svg>
                </div>
            </div>
        </motion.div>
    );

    // Wait message
    const WaitMessage = () => (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
            <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                transition={{ type: "spring", stiffness: 300, damping: 25 }}
                className="bg-white rounded-xl p-8 shadow-xl max-w-md w-full flex flex-col items-center"
            >
                <h2 className="text-2xl font-bold mb-4 text-gray-800 text-center">
                    Please wait here whilst your pass is being printed.
                </h2>
                <p className="text-lg text-gray-700 text-center">
                    Thank you
                </p>
            </motion.div>
        </div>
    );

    // Show wait message after photo is submitted
    const showWaitMessage = nextScreen && !showDisclaimer && !showCamera && !showPhotoPreview && photoBlob && !isSubmitting;

    return (
        <div className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden bg-gradient-to-br from-blue-100 to-blue-300">
            {/* Blurred, repeated background */}
           
            {/* Overlay */}
            <div className="relative z-10 flex flex-col items-center justify-center w-full flex-1">
                <motion.div
                    initial={{ y: 40, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ type: "spring", stiffness: 200, damping: 20 }}
                    className="bg-white bg-opacity-80 p-10 rounded-2xl shadow-2xl flex flex-col items-center max-w-md w-full"
                >
                    <h1 className="text-4xl font-bold mb-2 text-gray-800 tracking-tight">
                        Welcome To
                    </h1>
                    <h2 className="text-2xl font-semibold mb-8 text-blue-700">
                        {companyName}
                    </h2>
                    <input
                        type="text"
                        value={input}
                        onChange={(e) => {
                            const value = e.target.value;
                            setInput(value);
                            handleInputChange(value);
                        }}
                        placeholder="Please Enter Your Name"
                        className="w-full text-xl p-4 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                        disabled={nextScreen}
                    />
                </motion.div>
            </div>
            {/* Animated check circle */}
            <AnimatePresence>
                {nextScreen && !showDisclaimer && !showCamera && !showPhotoPreview && !showWaitMessage && <CheckCircle />}
            </AnimatePresence>
            {/* Modals */}
            <AnimatePresence>
                {showDisclaimer && <DisclaimerModal />}
            </AnimatePresence>
            {showCamera && <CameraModal />}
            {showPhotoPreview && <PhotoPreviewModal />}
            {showWaitMessage && <WaitMessage />}
            {/* Powered by StaffShark */}
            <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 text-gray-500 italic text-lg select-none pointer-events-none">
            </div>
        </div>
    );
};

export default VisitorLogin;
